<script setup lang="ts">
/**
 * GonkaKeeper sign-in button.
 *
 * Flow (ADR-036 challenge/verify, see services/hub/src/modules/auth/wallet.service.ts):
 *   1. Discover the Gonka Keeper provider, in priority order:
 *      (a) EIP-6963-style `gonka:announceProvider` event channel,
 *          matching BOTH `uuid` and `rdns` against the canonical Keeper
 *          constants. Race-free — every wallet announces independently
 *          with its own identity.
 *      (b) Synchronous probe of `window.gonkaKeeper` at MOUNT TIME,
 *          guarded by `isGonkaKeeper === true && providerId === 'gonka-keeper'`.
 *          Required because Keeper builds shipped before the EIP-6963
 *          rollout (≤ 0.1.6) don't dispatch the announce event. The
 *          identity flags are baked into Keeper's provider literal —
 *          a sibling wallet (Wallet GG) without them is filtered.
 *      The provider reference is captured ONCE at mount; we never
 *      re-read `window.gonkaKeeper` at click time, so a late
 *      `defineProperty` from another extension can't swap the target.
 *   2. POST /auth/wallet/challenge → { message, nonce, expiresAt }.
 *   3. provider.enable(chainId) + provider.getKey(chainId).
 *   4. provider.signArbitrary(chainId, bech32Address, message).
 *   5. POST /auth/wallet/verify { address, message, nonce, pubKeyB64, signatureB64 }.
 *   6. On 200 — backend issues secure-session cookie; we route to the dashboard.
 */
import { computed, onMounted, onBeforeUnmount, ref, shallowRef } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { authApi } from '../api/auth';
import { ApiError } from '../api/client';

interface Props {
  chainId: string;
  bech32Prefix: string;
}
const props = defineProps<Props>();

const auth = useAuthStore();
const router = useRouter();
const route = useRoute();

const detectionState = ref<'detecting' | 'found' | 'missing'>('detecting');
const busy = ref(false);
const error = ref<string | null>(null);

// EIP-6963 announce constants — must equal Keeper's PROVIDER_INFO in
// `wallets/gonka-keeper/src/provider/inpage.ts`. Changing either of these
// will break detection until Keeper is updated to match.
const KEEPER_UUID = 'e88a3b1c-6c4f-4b1a-9a3f-d76e87f5d39e';
const KEEPER_RDNS = 'ai.gonka.keeper';

// shallowRef so Vue's deep-reactivity proxy doesn't wrap the extension's
// methods (would break the `this` binding the extension relies on).
const providerRef = shallowRef<GonkaProvider | null>(null);

let detachListeners: (() => void) | null = null;

/**
 * Snapshot `window.gonkaKeeper` and verify it carries Keeper's identity
 * flags. The two flags (`isGonkaKeeper`, `providerId`) live inside
 * Keeper's provider literal — a sibling wallet that hijacked the
 * `gonkaKeeper` global without spoofing both flags is rejected here.
 */
function probeIdentifiedKeeper(): GonkaProvider | null {
  if (typeof window === 'undefined') return null;
  const candidate = window.gonkaKeeper as GonkaProvider | undefined;
  if (!candidate) return null;
  const flags = candidate as unknown as { isGonkaKeeper?: unknown; providerId?: unknown };
  if (flags.isGonkaKeeper !== true) return null;
  if (flags.providerId !== 'gonka-keeper') return null;
  return candidate;
}

onMounted(() => {
  let settled = false;
  let timeout: number | null = null;

  const settle = (state: 'found' | 'missing'): void => {
    if (settled) return;
    settled = true;
    detectionState.value = state;
    cleanup();
  };

  const onAnnounce = (e: CustomEvent<GonkaAnnounceEventDetail>): void => {
    const detail = e.detail;
    if (!detail || !detail.info || !detail.provider) return;
    if (detail.info.uuid !== KEEPER_UUID || detail.info.rdns !== KEEPER_RDNS) {
      return;
    }
    providerRef.value = detail.provider;
    settle('found');
  };

  const probeAndSettle = (): void => {
    const p = probeIdentifiedKeeper();
    if (p) {
      providerRef.value = p;
      settle('found');
    }
  };

  const onInitialized = (): void => probeAndSettle();

  const cleanup = (): void => {
    window.removeEventListener('gonka:announceProvider', onAnnounce as EventListener);
    window.removeEventListener('gonkaKeeper#initialized', onInitialized);
    if (timeout !== null) window.clearTimeout(timeout);
  };
  detachListeners = cleanup;

  // Path (a): new Keeper builds that emit EIP-6963 events.
  window.addEventListener('gonka:announceProvider', onAnnounce as EventListener);
  window.dispatchEvent(new CustomEvent('gonka:requestProvider'));

  // Path (b): older Keeper builds (≤ 0.1.6) without EIP-6963 still
  // dispatch `gonkaKeeper#initialized` right after assigning the global.
  // Both detection paths race; the first to find a valid provider wins.
  window.addEventListener('gonkaKeeper#initialized', onInitialized);
  probeAndSettle();

  // After 2 s of nothing valid, surface "Install Gonka Keeper".
  timeout = window.setTimeout(() => settle('missing'), 2000);
});

onBeforeUnmount(() => {
  if (detachListeners) detachListeners();
});

const buttonLabel = computed(() => {
  if (busy.value) return 'Signing…';
  if (detectionState.value === 'detecting') return 'Detecting wallet…';
  if (detectionState.value === 'missing') return 'Install Gonka Keeper';
  return 'Connect Gonka Keeper';
});

async function handleClick(): Promise<void> {
  if (detectionState.value === 'missing') {
    window.open('https://gonkakeeper.com', '_blank', 'noopener,noreferrer');
    return;
  }
  if (detectionState.value !== 'found' || busy.value) return;

  // Use the cached ref pinned at mount time. NEVER re-read
  // `window.gonkaKeeper` here — between mount and click a sibling
  // wallet may have overwritten the global.
  const provider = providerRef.value;
  if (!provider) {
    error.value = 'Wallet not detected.';
    return;
  }

  busy.value = true;
  error.value = null;
  try {
    const challenge = await authApi.walletChallenge();

    await provider.enable(props.chainId);
    const key = await provider.getKey(props.chainId);

    if (!key.bech32Address.startsWith(`${props.bech32Prefix}1`)) {
      throw new Error(
        `Wallet returned address with wrong prefix. Expected "${props.bech32Prefix}1…", got "${key.bech32Address.slice(0, 12)}…".`,
      );
    }

    const sig = await provider.signArbitrary(props.chainId, key.bech32Address, challenge.message);

    await authApi.walletVerify({
      address: key.bech32Address,
      message: challenge.message,
      nonce: challenge.nonce,
      pubKeyB64: sig.pub_key.value,
      signatureB64: sig.signature,
    });

    await auth.refresh();
    const next = typeof route.query.next === 'string' ? route.query.next : '/dashboard';
    await router.push(next);
  } catch (err) {
    if (err instanceof ApiError) {
      error.value = walletErrorMessage(err);
    } else {
      const msg = (err as Error).message || 'Wallet sign-in failed.';
      if (/reject|denied|cancel/i.test(msg)) {
        error.value = 'Signing was cancelled in the wallet.';
      } else {
        error.value = msg;
      }
    }
  } finally {
    busy.value = false;
  }
}

function walletErrorMessage(err: ApiError): string {
  switch (err.code) {
    case 'wallet_wrong_address_prefix':
      return 'Wrong address prefix — switch your wallet to the Gonka chain.';
    case 'wallet_message_missing_chain_id':
    case 'wallet_message_missing_origin':
      return 'Signed message is missing required binding. Refresh and try again.';
    case 'wallet_nonce_consumed':
    case 'wallet_nonce_expired':
      return 'Challenge expired. Click Connect again.';
    case 'wallet_invalid_encoding':
    case 'wallet_invalid_signature_length':
    case 'wallet_invalid_signature':
      return 'Wallet signature could not be verified.';
    default:
      return `Sign-in failed (${err.status}).`;
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <button
      class="btn-primary w-full"
      :disabled="busy || detectionState === 'detecting'"
      @click="handleClick"
    >
      <span>{{ buttonLabel }}</span>
    </button>
    <p v-if="detectionState === 'missing'" class="text-xs text-muted text-center">
      Extension not detected. Install Gonka Keeper and reload this page.
    </p>
    <p v-if="error" class="text-xs text-danger text-center">{{ error }}</p>
  </div>
</template>
